import React, { useState, useEffect } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { PropertyComparisonTable } from '@/components/PropertyComparisonTable';
import { useComparison } from '@/hooks/useComparison';
import { Property } from '@/types/property';
import axiosInstance from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Scale, ArrowLeft, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ComparisonPage() {
  const { compareIds, clearCompare, isLoading: isHookLoading } = useComparison();
  const [properties, setProperties] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProperties = async () => {
      if (compareIds.length === 0) {
        setProperties([]);
        setIsLoading(false);
        return;
      }
      try {
        const promises = compareIds.map(id => axiosInstance.get(`/api/properties/${id}`));
        const responses = await Promise.all(promises);
        setProperties(responses.map(r => r.data.property));
      } catch (error) {
        console.error('Error fetching properties for comparison:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isHookLoading) {
      fetchProperties();
    }
  }, [compareIds, isHookLoading]);

  return (
    <div className="min-h-screen bg-parchment">
      <Navbar />
      
      <main className="container mx-auto px-4 pt-32 pb-24">
        {/* Header Section */}
        <div className="mb-16 border-b-4 border-ink pb-12 flex flex-col md:flex-row justify-between items-end gap-8">
          <div className="max-w-2xl">
            <button 
              onClick={() => navigate('/propiedades')}
              className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-ink/40 hover:text-terracotta transition-colors mb-8 group"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Regresar al catálogo
            </button>
            <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-4 block">Herramienta B2B</span>
            <h1 className="text-5xl md:text-8xl font-display font-black uppercase italic leading-[0.85] text-ink">
              Comparativa <br /> 
              <span className="text-stroke-ink">de Mercado</span>
            </h1>
          </div>

          {properties.length > 0 && (
            <Button 
              variant="outline" 
              onClick={clearCompare}
              className="h-14 px-8 border-2 border-ink text-[10px] font-black uppercase tracking-widest group hover:bg-ink hover:text-parchment transition-all"
            >
              <Trash2 className="mr-3 h-4 w-4 group-hover:scale-110 transition-transform" />
              Limpiar Comparación
            </Button>
          )}
        </div>

        {/* Comparison Section */}
        {isLoading ? (
          <div className="py-40 text-center animate-pulse">
            <Scale className="h-12 w-12 mx-auto text-terracotta mb-4 animate-bounce" />
            <p className="font-display font-black uppercase tracking-widest text-ink">Analizando Datos...</p>
          </div>
        ) : properties.length > 0 ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-1000">
            <PropertyComparisonTable properties={properties} />
          </div>
        ) : (
          <div className="py-40 text-center brutalist-border bg-ink text-parchment relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 grain-texture pointer-events-none" />
            <Scale className="h-24 w-24 mx-auto text-terracotta mb-8" />
            <h3 className="text-4xl font-display font-black uppercase mb-4">Lista vacía</h3>
            <p className="text-xl font-serif italic text-parchment/60 mb-12 max-w-lg mx-auto leading-relaxed">
              Selecciona hasta 3 propiedades del catálogo para generar una comparativa profesional con análisis de valor.
            </p>
            <Button 
              variant="terracotta" 
              size="lg" 
              className="h-16 px-12 text-xs font-black tracking-widest"
              onClick={() => navigate('/propiedades')}
            >
              EXPLORAR CATÁLOGO
            </Button>
          </div>
        )}

        {/* Professional Footer for Comparison */}
        {properties.length > 0 && (
          <div className="mt-20 p-12 brutalist-border bg-ink text-parchment flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-md">
              <h4 className="text-xs font-black uppercase tracking-[0.4em] text-terracotta mb-4">Información de Valor</h4>
              <p className="text-sm font-serif italic text-parchment/60 leading-relaxed">
                Este análisis utiliza datos en tiempo real de nuestro ecosistema. Los valores destacados en naranja representan la opción más competitiva en cada categoría.
              </p>
            </div>
            <div className="flex gap-4">
              <Button variant="outline" className="h-14 px-8 border-parchment/20 text-parchment hover:bg-parchment hover:text-ink">Descargar PDF</Button>
              <Button variant="terracotta" className="h-14 px-8">Compartir con Cliente</Button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
